import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Trophy, Undo2, AlertCircle, Check, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import ConfirmModal from '../../components/ConfirmModal';
import { pingpongGameAPI } from './services/pingpongGameApi';
import { getStatusToPhaseMapping } from '../phaseConfig';

const SHOT_TYPES = [
  { value: 'ace',            label: 'Ace',            category: 'winner' },
  { value: 'smash',          label: 'Smash',          category: 'winner' },
  { value: 'spin',           label: 'Spin',           category: 'winner' },
  { value: 'fault',          label: 'Fault',          category: 'error'  },
  { value: 'unforced_error', label: 'Unforced Error', category: 'error'  },
];

const SHOT_COLORS = {
  ace:            { active: 'bg-purple-600 text-white ring-2 ring-purple-300', idle: 'bg-purple-100 text-purple-800 hover:bg-purple-200' },
  smash:          { active: 'bg-red-600 text-white ring-2 ring-red-300',       idle: 'bg-red-100 text-red-800 hover:bg-red-200' },
  spin:           { active: 'bg-teal-600 text-white ring-2 ring-teal-300',     idle: 'bg-teal-100 text-teal-800 hover:bg-teal-200' },
  fault:          { active: 'bg-orange-600 text-white ring-2 ring-orange-300', idle: 'bg-orange-100 text-orange-800 hover:bg-orange-200' },
  unforced_error: { active: 'bg-amber-600 text-white ring-2 ring-amber-300',   idle: 'bg-amber-100 text-amber-800 hover:bg-amber-200' },
};

const PingPongScoring = ({
  gameCommonId,
  gameSettings,
  participantAInfo,
  participantBInfo,
  onGameComplete,
  onBack,
  onGamePointsStatus,
  event,
  isScorer = true,
  paused = false,
}) => {
  const { theme } = useTheme();
  const isModularFlow = !!gameCommonId;

  useEffect(() => {}, [event?.status]);

  const [currentPhase, setCurrentPhase] = useState('scoring');
  const STATUS_TO_PHASE = getStatusToPhaseMapping('pingpong');

  const [game, setGame] = useState(null);
  const [gameId, setGameId] = useState(gameCommonId || null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  const [detailedScoring, setDetailedScoring] = useState(false);
  const [pendingWinner, setPendingWinner] = useState(null);
  const [selectedShotType, setSelectedShotType] = useState(null);
  const [scoredByPlayerId, setScoredByPlayerId] = useState(null);
  const [errorByPlayerId, setErrorByPlayerId] = useState(null);

  const [serveChosen, setServeChosen] = useState(false);

  // Holds summary of the just-completed game while waiting for user to start the next one
  const [completedGameSummary, setCompletedGameSummary] = useState(null);

  // Detect doubles from format OR from player structure
  const isDoublesGame = game?.format === 'doubles'
    || game?.playerA?.type === 'team'
    || (game?.playerA?.players?.length || 0) > 1;
  const playersPerTeam = isDoublesGame ? 2 : 1;

  const initializingRef = useRef(false);

  useEffect(() => {
    const loadOrCreateGame = async () => {
      if (isModularFlow && gameCommonId) {
        try {
          setLoading(true);
          const existingGame = await pingpongGameAPI.getById(gameCommonId);
          setGame(existingGame);
          setGameId(existingGame.id);

          const hasRallies = (existingGame.rallies?.length || 0) > 0;
          const isDoubles  = existingGame.format === 'doubles'
            || existingGame.playerA?.type === 'team'
            || (existingGame.playerA?.players?.length || 0) > 1;
          const serverKnown   = existingGame.servingPlayerId != null;
          const receiverKnown = existingGame.receivingPlayerId != null;
          if (hasRallies || (serverKnown && (!isDoubles || receiverKnown))) {
            setServeChosen(true);
          }

          if (onGamePointsStatus) onGamePointsStatus((existingGame.rallies?.length || 0) > 0);

          if (existingGame.status === 'completed') {
            setCurrentPhase('completed');
          } else {
            setCurrentPhase('scoring');
          }
        } catch (error) {
          console.error('[PingPongScoring] Failed to load game:', error);
          setError('Failed to load game. Please try again.');
        } finally {
          setLoading(false);
        }
        return;
      }
    };

    loadOrCreateGame();
  }, [isModularFlow, gameCommonId]);

  const isMatchComplete = useCallback(() => {
    if (!game) return false;
    const { currentGameScoreA, currentGameScoreB, maxPoints, winBy2 } = game;
    if (winBy2) {
      return (currentGameScoreA >= maxPoints && currentGameScoreA - currentGameScoreB >= 2) ||
             (currentGameScoreB >= maxPoints && currentGameScoreB - currentGameScoreA >= 2);
    }
    return currentGameScoreA >= maxPoints || currentGameScoreB >= maxPoints;
  }, [game]);

  const isGameComplete = useCallback(() => {
    if (!game) return false;
    const matchesToWin = Math.ceil(game.bestOf / 2);
    return game.playerAMatchesWon >= matchesToWin || game.playerBMatchesWon >= matchesToWin;
  }, [game]);

  const getPlayerName = useCallback((playerId) => {
    if (!playerId || !game) return null;
    const id = parseInt(playerId, 10);
    if (parseInt(game.playerA?.id, 10) === id) return game.playerA?.name;
    if (parseInt(game.playerB?.id, 10) === id) return game.playerB?.name;
    for (const p of (game.playerA?.players || [])) {
      if (parseInt(p.id, 10) === id) return p.name;
    }
    for (const p of (game.playerB?.players || [])) {
      if (parseInt(p.id, 10) === id) return p.name;
    }
    return null;
  }, [game]);

  const resetDetailState = useCallback(() => {
    setPendingWinner(null);
    setSelectedShotType(null);
    setScoredByPlayerId(null);
    setErrorByPlayerId(null);
  }, []);

  const handlePointScored = useCallback(async (winner, detail = {}) => {
    if (!game || !gameId || isMatchComplete() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await pingpongGameAPI.recordRally(gameId, {
        pointWinner: winner === 1 ? 'A' : 'B',
        ...detail
      });
      setGame(result.game);
      resetDetailState();
      if ((game.rallies?.length || 0) === 0 && onGamePointsStatus) onGamePointsStatus(true);
    } catch (err) {
      console.error('[PingPongScoring] Failed to record point:', err);
      setError('Failed to save point. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [game, gameId, isMatchComplete, saving, resetDetailState]);

  const handleServiceFault = useCallback(async () => {
    if (!game || !gameId || saving) return;
    const winnerSide = game.servingPlayer === 'A' ? 'B' : 'A';
    setSaving(true);
    setError(null);
    try {
      const result = await pingpongGameAPI.recordRally(gameId, {
        pointWinner: winnerSide,
        shotType: 'fault',
      });
      setGame(result.game);
      resetDetailState();
      if ((game.rallies?.length || 0) === 0 && onGamePointsStatus) onGamePointsStatus(true);
    } catch (err) {
      setError('Failed to record service fault.');
    } finally {
      setSaving(false);
    }
  }, [game, gameId, saving, resetDetailState, onGamePointsStatus]);

  // Tap a scoring button — simple mode records immediately, detail mode opens panel
  const handleScoringButtonTap = useCallback((side, playerId) => {
    if (isMatchComplete() || saving) return;
    const winnerInt = side === 'A' ? 1 : 2;
    if (detailedScoring) {
      setPendingWinner(side);
      setScoredByPlayerId(playerId || null);
      setErrorByPlayerId(null);
      if (selectedShotType === 'ace' && side !== game?.servingPlayer) {
        setSelectedShotType(null);
      }
    } else {
      handlePointScored(winnerInt, { scoredByPlayerId: playerId });
    }
  }, [isMatchComplete, saving, detailedScoring, game?.servingPlayer, handlePointScored, selectedShotType]);

  const handleUndo = useCallback(async () => {
    if (!game || !gameId || (game.rallies?.length || 0) === 0 || saving) return;
    setSaving(true);
    setError(null);
    try {
      const updatedGame = await pingpongGameAPI.undoLastRally(gameId);
      setGame(updatedGame);
      if ((game.rallies?.length || 0) === 1 && onGamePointsStatus) onGamePointsStatus(false);
    } catch (err) {
      console.error('Failed to undo:', err);
      setError('Failed to undo. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [game, gameId, saving, onGamePointsStatus]);

  const handleCompleteMatch = useCallback(async () => {
    if (!game || !gameId || !isMatchComplete() || saving) return;
    const winnerPlayer = game.currentGameScoreA > game.currentGameScoreB ? 'A' : 'B';

    // Capture current game data before API call
    const completedGameNum = game.currentMatch;
    const completedScoreA  = game.currentGameScoreA;
    const completedScoreB  = game.currentGameScoreB;

    setSaving(true);
    setError(null);
    try {
      const response = await pingpongGameAPI.completeMatch(gameId, { winnerPlayer });
      const updatedGame = response.game || response;
      setGame(updatedGame);
      if (updatedGame.status === 'completed') {
        setCurrentPhase('completed');
      } else {
        // Match continues — show between-games screen
        setCompletedGameSummary({
          gameNumber:        completedGameNum,
          playerAScore:      completedScoreA,
          playerBScore:      completedScoreB,
          playerAMatchesWon: updatedGame.playerAMatchesWon,
          playerBMatchesWon: updatedGame.playerBMatchesWon,
          nextGameNumber:    updatedGame.currentMatch,
        });
        resetDetailState();
      }
    } catch (err) {
      console.error('Failed to complete match:', err);
      setError('Failed to complete match. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [game, gameId, isMatchComplete, saving, resetDetailState]);

  const handleCompleteEvent = async () => {
    if (!game) return;
    const playerAWon = game.playerAMatchesWon > game.playerBMatchesWon;
    let winner = playerAWon ? game.playerA : game.playerB;
    const result = {
      winnerId: winner?.id,
      winnerType: winner?.type || 'user',
      scoreSummary: `${game.playerAMatchesWon}-${game.playerBMatchesWon}`,
      playerAScore: game.playerAMatchesWon,
      playerBScore: game.playerBMatchesWon,
      winner,
      gameId: game.id
    };
    if (onGameComplete) await onGameComplete(result);
  };


  const handleRefresh = async () => {
    if (!gameId) return;
    try {
      setSaving(true);
      const refreshed = await pingpongGameAPI.getById(gameId);
      setGame(refreshed);
      if (refreshed.status === 'completed') setCurrentPhase('completed');
      toast.success('Score refreshed');
    } catch (err) {
      toast.error('Failed to refresh score');
    } finally {
      setSaving(false);
    }
  };

  const handleResetMatch = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Reset Match',
      message: 'Are you sure you want to reset this match? All rallies will be deleted and scoring will restart from zero.',
      confirmText: 'Reset',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false });
        try {
          const resetGame = await pingpongGameAPI.resetGame(gameId);
          setGame(resetGame);
          setCurrentPhase('scoring');
          setServeChosen(false);
          setCompletedGameSummary(null);
          resetDetailState();
          toast.success('Match reset — ready to start scoring again');
        } catch (err) {
          toast.error('Failed to reset match');
        }
      },
      onCancel: () => setConfirmModal({ isOpen: false })
    });
  };

  const handleServeSelected = useCallback(async (servingPlayer, servingPlayerId) => {
    try {
      setSaving(true);
      setError(null);
      const updatedGame = await pingpongGameAPI.setInitialServe(gameId, servingPlayer, servingPlayerId);
      setGame(updatedGame);
      setServeChosen(true);
    } catch (err) {
      console.error('[PingPongScoring] Failed to set serving player:', err);
      setError('Failed to set serving player. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [gameId]);

  const renderRallyHeaders = () => (
    <div className={`grid grid-cols-4 text-xs ${theme.textMuted} py-1 border-b ${theme.border} mb-1`}>
      <span>Point</span>
      <span className="text-center">Scored by</span>
      <span className="text-center">Error by</span>
      <span className="text-right">Score</span>
    </div>
  );

  const renderRallyRows = (rallies) => {
    const sorted = [...rallies].reverse();
    const rows = [];
    let lastMatchNumber = null;
    sorted.forEach((rally) => {
      if (rally.matchNumber !== lastMatchNumber) {
        lastMatchNumber = rally.matchNumber;
        rows.push(
          <div key={`game-header-${rally.matchNumber}`} className={`col-span-4 text-xs font-semibold ${theme.textMuted} bg-opacity-30 px-1 py-0.5 mt-1 mb-0.5`}>
            Game {rally.matchNumber}
          </div>
        );
      }
      const scorerIsA  = rally.pointWinner === 'A';
      const newScoreA  = rally.playerAScoreBefore + (scorerIsA ? 1 : 0);
      const newScoreB  = rally.playerBScoreBefore + (!scorerIsA ? 1 : 0);
      const isError    = ['fault', 'unforced_error'].includes(rally.shotType);
      const scorerTeam = scorerIsA ? game.playerA : game.playerB;
      const loserTeam  = scorerIsA ? game.playerB : game.playerA;
      const scorerName = getPlayerName(rally.pointWinnerPlayerId) || scorerTeam?.name;
      const loserName  = getPlayerName(rally.pointLoserPlayerId) || (isError ? loserTeam?.name : null);
      rows.push(
        <div key={rally.id} className={`grid grid-cols-4 text-xs py-1.5 border-b ${theme.border} last:border-0 items-center`}>
          <span className={theme.textMuted}>#{rally.rallySequence}</span>
          <span className={`text-center truncate ${scorerIsA ? 'text-green-600' : 'text-blue-600'}`}>
            {scorerName}
            {rally.shotType && !isError && <span className="ml-1 opacity-70">({rally.shotType})</span>}
          </span>
          <span className={`text-center truncate ${isError ? 'text-red-500' : theme.textMuted}`}>
            {isError ? (loserName || '—') : '—'}
          </span>
          <span className={`text-right font-medium ${theme.text}`}>{newScoreA} - {newScoreB}</span>
        </div>
      );
    });
    return rows;
  };

  const renderScoringPhase = () => {
    if (!game) return null;

    // Between-games screen — shown after a game completes but before the next starts
    if (completedGameSummary) {
      const aWon = completedGameSummary.playerAScore > completedGameSummary.playerBScore;
      const winnerName = aWon ? game.playerA?.name : game.playerB?.name;
      return (
        <div className="space-y-4">
          <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center space-y-3`}>
            <Trophy className="w-12 h-12 mx-auto text-yellow-500" />
            <div className={`text-lg font-bold ${theme.text}`}>
              Game {completedGameSummary.gameNumber} Complete
            </div>
            <div className={`text-3xl font-bold ${theme.text}`}>
              {completedGameSummary.playerAScore} – {completedGameSummary.playerBScore}
            </div>
            <div className={`text-sm ${theme.textMuted}`}>
              <span className="font-semibold">{winnerName}</span> wins this game
            </div>
            <div className={`text-sm ${theme.textMuted}`}>
              Series: {game.playerA?.name} <span className="font-bold">{completedGameSummary.playerAMatchesWon}</span>
              {' – '}
              <span className="font-bold">{completedGameSummary.playerBMatchesWon}</span> {game.playerB?.name}
            </div>
          </div>
          {isScorer && (
            <button
              onClick={() => {
                setCompletedGameSummary(null);
                setServeChosen(false);
              }}
              className={`w-full ${theme.accent} ${theme.accentHover} text-white py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2`}
            >
              <RefreshCw className="w-5 h-5" />
              Start Game {completedGameSummary.nextGameNumber}
            </button>
          )}
        </div>
      );
    }

    const matchComplete = isMatchComplete();
    const ralliesCount = game.rallies?.length || 0;
    const teamAPlayers = isDoublesGame ? (game.playerA?.players || []) : [];
    const teamBPlayers = isDoublesGame ? (game.playerB?.players || []) : [];
    const isErrorShot  = ['fault', 'unforced_error'].includes(selectedShotType);
    const winnerInt    = pendingWinner === 'A' ? 1 : 2;

    // Current server display name — prefer individual player, fall back to first player of serving side
    const servingSideTeam = game.servingPlayer === 'A' ? game.playerA : game.playerB;
    const serverName = getPlayerName(game.servingPlayerId)
      || servingSideTeam?.players?.[0]?.name
      || servingSideTeam?.name;

    return (
      <div className="space-y-4 min-h-[60vh]">

        {/* Game Header */}
        <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
          <div className="text-center">
            {game.bestOf > 1 && (
              <div className={`text-sm ${theme.textMuted} mb-1`}>
                Game {game.currentMatch} of {game.bestOf}
              </div>
            )}
            <div className={`text-lg font-semibold ${theme.text}`}>
              {game.playerA?.name} ({game.playerAMatchesWon}) vs {game.playerB?.name} ({game.playerBMatchesWon})
            </div>
          </div>
          {game.status !== 'paused' && (
            <div className="flex justify-center mt-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                LIVE
              </span>
            </div>
          )}
        </div>

        {/* Score Display — static, not clickable */}
        <div className="grid grid-cols-2 gap-4">
          {/* Side A score card */}
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4 text-center`}>
            <div className={`text-6xl font-bold ${theme.text} mb-1`}>{game.currentGameScoreA}</div>
            {game.bestOf > 1 && (
              <div className={`text-xs ${theme.textMuted} mb-1`}>Games: {game.playerAMatchesWon}</div>
            )}
            <div className="flex justify-center mt-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                game.servingPlayer === 'A' ? 'ring-2 ring-red-500 text-red-500' : theme.textMuted
              }`}>
                {game.servingPlayer === 'A' && <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse flex-shrink-0" />}
                {game.playerA?.name}
              </span>
            </div>
          </div>

          {/* Side B score card */}
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4 text-center`}>
            <div className={`text-6xl font-bold ${theme.text} mb-1`}>{game.currentGameScoreB}</div>
            {game.bestOf > 1 && (
              <div className={`text-xs ${theme.textMuted} mb-1`}>Games: {game.playerBMatchesWon}</div>
            )}
            <div className="flex justify-center mt-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                game.servingPlayer === 'B' ? 'ring-2 ring-red-500 text-red-500' : theme.textMuted
              }`}>
                {game.servingPlayer === 'B' && <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse flex-shrink-0" />}
                {game.playerB?.name}
              </span>
            </div>
          </div>
        </div>

        {/* Mid-game serve selection — doubles only, when serve changes teams */}
        {isScorer && serveChosen && isDoublesGame && !game.servingPlayerId && !matchComplete && (
          <div className={`${theme.card} rounded-lg border-2 border-yellow-500 p-4 space-y-3`}>
            <h3 className={`${theme.text} font-semibold text-center`}>
              Who is serving for {game.servingPlayer === 'A' ? game.playerA?.name : game.playerB?.name}?
            </h3>
            <div className="flex gap-3">
              {(game.servingPlayer === 'A' ? teamAPlayers : teamBPlayers).map(player => {
                const firstName = player.name?.split(' ')[0] || player.name;
                const bgClass = game.servingPlayer === 'A'
                  ? 'bg-green-600 hover:bg-green-700 active:bg-green-800'
                  : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800';
                return (
                  <button
                    key={player.id}
                    onClick={() => handleServeSelected(game.servingPlayer, player.id)}
                    disabled={saving || paused}
                    className={`flex-1 ${bgClass} active:scale-95 text-white py-4 rounded-lg font-semibold transition-transform disabled:opacity-50`}
                  >
                    {firstName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Server info — shown after serve is chosen and player is known */}
        {serveChosen && serverName && (!isDoublesGame || game.servingPlayerId) && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-3`}>
            <div className={`text-center text-sm ${theme.text}`}>
              <span className={theme.textMuted}>Server: </span>
              <span className="font-semibold">{serverName}</span>
            </div>
          </div>
        )}

        {/* Serve selection — shown before first rally, scorer only */}
        {isScorer && !serveChosen && !matchComplete && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-4`}>
            <h3 className={`${theme.text} font-semibold text-center`}>Who is serving first?</h3>
            {isDoublesGame ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-3">
                  <div className="text-xs font-semibold text-center uppercase tracking-wide text-green-500 truncate">
                    {game.playerA?.name}
                  </div>
                  {teamAPlayers.map(player => {
                    const firstName = player.name?.split(' ')[0] || player.name;
                    return (
                      <button
                        key={player.id}
                        onClick={() => handleServeSelected('A', player.id)}
                        disabled={saving || paused}
                        className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 active:scale-95 text-white py-5 rounded-lg font-semibold transition-transform disabled:opacity-50"
                      >
                        <span className="sm:hidden">{firstName.slice(0, 3)}</span>
                        <span className="hidden sm:inline">{firstName}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-col gap-3">
                  <div className="text-xs font-semibold text-center uppercase tracking-wide text-blue-400 truncate">
                    {game.playerB?.name}
                  </div>
                  {teamBPlayers.map(player => {
                    const firstName = player.name?.split(' ')[0] || player.name;
                    return (
                      <button
                        key={player.id}
                        onClick={() => handleServeSelected('B', player.id)}
                        disabled={saving || paused}
                        className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-95 text-white py-5 rounded-lg font-semibold transition-transform disabled:opacity-50"
                      >
                        <span className="sm:hidden">{firstName.slice(0, 3)}</span>
                        <span className="hidden sm:inline">{firstName}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleServeSelected('A', game.playerA?.id)}
                  disabled={saving || paused}
                  className="py-4 px-4 rounded-lg font-semibold text-lg transition-colors bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                >
                  {game.playerA?.name}
                </button>
                <button
                  onClick={() => handleServeSelected('B', game.playerB?.id)}
                  disabled={saving || paused}
                  className="py-4 px-4 rounded-lg font-semibold text-lg transition-colors bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                >
                  {game.playerB?.name}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Viewer waiting for serve selection */}
        {!isScorer && !serveChosen && !matchComplete && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
            <p className={theme.textMuted}>Waiting for scorer to set serving order...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Detailed Scoring Toggle — scorer only, after serve chosen */}
        {isScorer && serveChosen && !matchComplete && (
          <label className={`flex items-center gap-2 text-sm ${theme.textMuted} cursor-pointer select-none`}>
            <input
              type="checkbox"
              checked={detailedScoring}
              onChange={e => { setDetailedScoring(e.target.checked); resetDetailState(); }}
              className="rounded"
            />
            <span>Enable Detailed Scoring</span>
          </label>
        )}

        {/* Complete Match button — when set is won */}
        {isScorer && serveChosen && matchComplete && (
          <button
            onClick={handleCompleteMatch}
            disabled={saving || paused}
            className={`w-full ${theme.accent} ${theme.accentHover} py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2`}
          >
            {saving ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            ) : (
              <>
                <Trophy className="w-5 h-5" />
                {isGameComplete() ? 'Complete Match' : `Complete Game ${game.currentMatch}`}
              </>
            )}
          </button>
        )}

        {/* Scoring buttons — shown when serve chosen, not complete, and server is known for doubles */}
        {isScorer && serveChosen && !matchComplete && (!isDoublesGame || game.servingPlayerId) && (
          <div className="space-y-4">
            {isDoublesGame ? (
              <div className="grid grid-cols-2 gap-3">
                {/* Team A column */}
                <div className="flex flex-col gap-3">
                  <div className="text-xs font-semibold text-center uppercase tracking-wide text-green-500 truncate">
                    {game.playerA?.name}
                  </div>
                  {teamAPlayers.map(player => {
                    const firstName = player.name?.split(' ')[0] || player.name;
                    const isServer = player.id === game.servingPlayerId;
                    return (
                      <button
                        key={player.id}
                        onClick={() => handleScoringButtonTap('A', player.id)}
                        disabled={saving || paused}
                        className={`w-full bg-green-600 hover:bg-green-700 active:bg-green-800 active:scale-95 text-white py-5 rounded-lg font-semibold transition-transform disabled:opacity-50 ${isServer ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-transparent' : ''}`}
                      >
                        <span className="sm:hidden">{firstName.slice(0, 3)}</span>
                        <span className="hidden sm:inline">{firstName}</span>
                      </button>
                    );
                  })}
                </div>
                {/* Team B column */}
                <div className="flex flex-col gap-3">
                  <div className="text-xs font-semibold text-center uppercase tracking-wide text-blue-400 truncate">
                    {game.playerB?.name}
                  </div>
                  {teamBPlayers.map(player => {
                    const firstName = player.name?.split(' ')[0] || player.name;
                    const isServer = player.id === game.servingPlayerId;
                    return (
                      <button
                        key={player.id}
                        onClick={() => handleScoringButtonTap('B', player.id)}
                        disabled={saving || paused}
                        className={`w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-95 text-white py-5 rounded-lg font-semibold transition-transform disabled:opacity-50 ${isServer ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-transparent' : ''}`}
                      >
                        <span className="sm:hidden">{firstName.slice(0, 3)}</span>
                        <span className="hidden sm:inline">{firstName}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleScoringButtonTap('A', game.playerA?.id)}
                  disabled={saving || paused}
                  className={`bg-green-600 hover:bg-green-700 active:bg-green-800 active:scale-95 text-white py-6 rounded-lg transition-transform disabled:opacity-50 ${game.servingPlayer === 'A' ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-transparent' : ''}`}
                >
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="font-semibold text-lg">{game.playerA?.name?.split(' ')[0] || game.playerA?.name}</span>
                    <span className="text-xs font-normal opacity-70">tap to score</span>
                  </div>
                </button>
                <button
                  onClick={() => handleScoringButtonTap('B', game.playerB?.id)}
                  disabled={saving || paused}
                  className={`bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-95 text-white py-6 rounded-lg transition-transform disabled:opacity-50 ${game.servingPlayer === 'B' ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-transparent' : ''}`}
                >
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="font-semibold text-lg">{game.playerB?.name?.split(' ')[0] || game.playerB?.name}</span>
                    <span className="text-xs font-normal opacity-70">tap to score</span>
                  </div>
                </button>
              </div>
            )}

            {/* Detail scoring panel — visible as soon as detailedScoring is on */}
            {detailedScoring && (
              <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-4`}>
                {/* Header — only when a player has been tapped */}
                {pendingWinner != null ? (
                  <div className="flex items-center justify-between">
                    <h3 className={`${theme.text} font-semibold`}>
                      {(isDoublesGame && scoredByPlayerId
                        ? getPlayerName(scoredByPlayerId)
                        : (pendingWinner === 'A' ? game.playerA?.name : game.playerB?.name)
                      )} won point
                    </h3>
                    <button onClick={resetDetailState} className={`text-sm ${theme.textMuted}`}>Cancel</button>
                  </div>
                ) : (
                  <p className={`text-sm text-center ${theme.textMuted}`}>Tap a player button above to select the winner</p>
                )}

                {/* Winning shot types — click to submit directly */}
                <div>
                  <div className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-1.5`}>Winning Shot</div>
                  <div className="grid grid-cols-3 gap-2">
                    {SHOT_TYPES.filter(s => s.category === 'winner').map(shot => {
                      const colors = SHOT_COLORS[shot.value];
                      const isAce = shot.value === 'ace';
                      // Ace: disabled only when a non-server is selected as winner
                      const isAceBlocked = isAce && pendingWinner != null && pendingWinner !== game.servingPlayer;
                      // Non-ace: disabled when no winner selected yet
                      const disabled = isAce ? isAceBlocked : (pendingWinner == null);
                      return (
                        <button
                          key={shot.value}
                          disabled={disabled || saving}
                          onClick={() => {
                            if (isAce) {
                              // Always assign ace to the server, regardless of pendingWinner
                              handlePointScored(game.servingPlayer === 'A' ? 1 : 2, {
                                shotType: 'ace',
                                scoredByPlayerId: game.servingPlayerId
                              });
                            } else {
                              handlePointScored(winnerInt, {
                                shotType: shot.value,
                                scoredByPlayerId: isDoublesGame ? scoredByPlayerId : null
                              });
                            }
                          }}
                          className={`py-2 px-2 rounded-lg text-sm font-semibold transition-all ${
                            disabled
                              ? 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400'
                              : colors.idle
                          }`}
                        >
                          {shot.label}
                          {isAce && isAceBlocked && (
                            <span className="block text-xs opacity-60">not server</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Error types */}
                <div>
                  <div className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-1.5`}>Opponent Error</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {/* Service Fault — always enabled, assigns point to non-server */}
                    <button
                      onClick={handleServiceFault}
                      disabled={saving}
                      className="py-2 px-2 rounded-lg text-sm font-semibold transition-all bg-rose-100 text-rose-800 hover:bg-rose-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Service Fault
                    </button>
                    {SHOT_TYPES.filter(s => s.category === 'error').map(shot => {
                      const colors = SHOT_COLORS[shot.value];
                      const noWinner = pendingWinner == null;
                      return (
                        <button
                          key={shot.value}
                          disabled={noWinner || saving}
                          onClick={() => {
                            if (!isDoublesGame) {
                              const loserSide = pendingWinner === 'A' ? game.playerB : game.playerA;
                              handlePointScored(winnerInt, {
                                shotType: shot.value,
                                errorByPlayerId: loserSide?.id
                              });
                            } else {
                              setSelectedShotType(shot.value);
                              setErrorByPlayerId(null);
                            }
                          }}
                          className={`py-2 px-2 rounded-lg text-sm font-semibold transition-all ${
                            noWinner
                              ? 'opacity-40 cursor-not-allowed ' + colors.idle
                              : selectedShotType === shot.value ? colors.active : colors.idle
                          }`}
                        >
                          {shot.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Doubles: who made the error? */}
                {isDoublesGame && selectedShotType && isErrorShot && (
                  <div>
                    <div className={`text-sm font-medium ${theme.textMuted} mb-2`}>Who made the error?</div>
                    <div className="grid grid-cols-2 gap-2">
                      {(pendingWinner === 'A' ? teamBPlayers : teamAPlayers).filter(p => p).map(player => (
                        <button
                          key={player.id}
                          onClick={() => handlePointScored(winnerInt, {
                            shotType: selectedShotType,
                            scoredByPlayerId,
                            errorByPlayerId: player.id
                          })}
                          disabled={saving}
                          className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${theme.secondary} ${theme.secondaryHover} ${theme.text}`}
                        >
                          {player.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Undo — scorer only, after serve chosen, not in detail mode */}
        {isScorer && serveChosen && !matchComplete && !pendingWinner && !paused && (
          <button
            onClick={handleUndo}
            disabled={ralliesCount === 0 || saving}
            className={`w-full ${theme.secondary} ${theme.secondaryHover} ${theme.text} py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50`}
          >
            <Undo2 className="w-4 h-4" />
            Undo Last
          </button>
        )}

        {/* Undo when complete */}
        {isScorer && matchComplete && (
          <button
            onClick={handleUndo}
            disabled={ralliesCount === 0 || saving}
            className={`w-full ${theme.secondary} ${theme.secondaryHover} ${theme.text} py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50`}
          >
            <Undo2 className="w-4 h-4" />
            Undo Last Point
          </button>
        )}

        {/* Saving indicator */}
        <div className="h-6 flex items-center justify-center">
          {saving && (
            <div className={`text-sm ${theme.textMuted} flex items-center gap-2`}>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
              Saving...
            </div>
          )}
        </div>

        {/* Rally History */}
        {ralliesCount > 0 && (
          <details className={`${theme.card} rounded-lg border ${theme.border}`}>
            <summary className={`${theme.text} p-4 cursor-pointer font-medium`}>
              Point History ({ralliesCount} points)
            </summary>
            <div className="px-3 pb-3">
              {renderRallyHeaders()}
              <div className="max-h-40 overflow-y-auto">
                {renderRallyRows(game.rallies || [])}
              </div>
            </div>
          </details>
        )}
      </div>
    );
  };

  const renderCompletedPhase = () => {
    if (!game) return null;

    const playerAWon = game.playerAMatchesWon > game.playerBMatchesWon;
    const winnerName = game.winner?.name || (playerAWon ? game.playerA?.name : game.playerB?.name);
    const loserName = playerAWon ? game.playerB?.name : game.playerA?.name;
    const isGameCompleted = game?.status === 'completed';
    const isEventCompleted = event?.status === 'completed';
    const showCompleteEventButton = isGameCompleted && !isEventCompleted;
    const totalRallies = game.rallies?.length || 0;
    const playerATotalPoints = (game.rallies || []).filter(r => r.pointWinner === 'A').length;
    const playerBTotalPoints = (game.rallies || []).filter(r => r.pointWinner === 'B').length;

    return (
      <div className="space-y-6">
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
          <Trophy className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
          <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>{winnerName} Wins!</h2>
          <div className={`text-lg ${theme.textMuted} mb-2`}>
            defeated <span className={theme.text}>{loserName}</span>
          </div>
          <div className={`text-xl font-semibold ${theme.text}`}>
            {game.playerAMatchesWon} - {game.playerBMatchesWon}
          </div>
          <div className={`text-sm ${theme.textMuted} mt-1`}>
            Best of {game.bestOf} | {isDoublesGame ? 'Doubles' : 'Singles'}
          </div>
        </div>

        <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
          <h3 className={`${theme.text} font-semibold mb-4`}>Match Summary</h3>
          <div className={`grid grid-cols-3 gap-2 mb-4 pb-3 border-b ${theme.border}`}>
            <div className="text-center">
              <div className={`${theme.text} ${playerAWon ? 'text-green-600 font-semibold' : ''}`}>
                {game.playerA?.name}
                {playerAWon && <Trophy className="w-4 h-4 inline ml-1 text-yellow-500" />}
              </div>
              <div className={`text-2xl font-bold ${playerAWon ? 'text-green-600' : theme.text}`}>{game.playerAMatchesWon}</div>
              <div className={`text-xs ${theme.textMuted}`}>games won</div>
            </div>
            <div className={`text-center ${theme.textMuted} flex items-center justify-center text-xl`}>VS</div>
            <div className="text-center">
              <div className={`${theme.text} ${!playerAWon ? 'text-green-600 font-semibold' : ''}`}>
                {game.playerB?.name}
                {!playerAWon && <Trophy className="w-4 h-4 inline ml-1 text-yellow-500" />}
              </div>
              <div className={`text-2xl font-bold ${!playerAWon ? 'text-green-600' : theme.text}`}>{game.playerBMatchesWon}</div>
              <div className={`text-xs ${theme.textMuted}`}>games won</div>
            </div>
          </div>

          <div className={`grid grid-cols-3 gap-2 mb-4 pb-3 border-b ${theme.border}`}>
            <div className={`text-center text-lg font-semibold ${theme.text}`}>{playerATotalPoints}</div>
            <div className={`text-center text-sm ${theme.textMuted}`}>Total Points</div>
            <div className={`text-center text-lg font-semibold ${theme.text}`}>{playerBTotalPoints}</div>
          </div>

          <h4 className={`${theme.textMuted} text-sm font-medium mb-3`}>Game-by-Game Scores</h4>
          <div className="space-y-2">
            {(game.matches || []).map((match) => {
              const gameWinnerIsA = match.winnerPlayer === 'A';
              return (
                <div key={match.id} className={`grid grid-cols-3 gap-2 p-3 rounded-lg ${theme.backgroundSecondary}`}>
                  <div className={`text-center font-semibold ${gameWinnerIsA ? 'text-green-600' : theme.text}`}>{match.playerAScore}</div>
                  <div className={`text-center text-sm ${theme.textMuted}`}>Game {match.matchNumber}</div>
                  <div className={`text-center font-semibold ${!gameWinnerIsA ? 'text-green-600' : theme.text}`}>{match.playerBScore}</div>
                </div>
              );
            })}
          </div>
        </div>

        {totalRallies > 0 && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
            <h3 className={`${theme.text} font-semibold mb-4`}>Point History ({totalRallies} total points)</h3>
            <div className="space-y-3">
              {(game.matches || []).map((match) => {
                const matchRallies = (game.rallies || []).filter(r => r.matchNumber === match.matchNumber);
                if (matchRallies.length === 0) return null;
                const gameWinnerIsA = match.winnerPlayer === 'A';
                const gameWinnerName = gameWinnerIsA ? game.playerA?.name : game.playerB?.name;
                return (
                  <details key={match.matchNumber} className={`${theme.backgroundSecondary} rounded-lg border ${theme.border}`}
                    open={match.matchNumber === (game.matches?.length || 1)}>
                    <summary className="p-3 cursor-pointer flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${theme.text}`}>Game {match.matchNumber}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${gameWinnerIsA ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                          {gameWinnerName} won
                        </span>
                      </div>
                      <div className={`font-semibold ${theme.text}`}>
                        <span className={gameWinnerIsA ? 'text-green-600' : ''}>{match.playerAScore}</span>
                        <span className={theme.textMuted}> - </span>
                        <span className={!gameWinnerIsA ? 'text-green-600' : ''}>{match.playerBScore}</span>
                      </div>
                    </summary>
                    <div className="px-3 pb-3">
                      {renderRallyHeaders()}
                      <div className="max-h-40 overflow-y-auto">{renderRallyRows(matchRallies)}</div>
                      <div className={`mt-2 pt-2 border-t ${theme.border} text-xs ${theme.textMuted} flex justify-between`}>
                        <span>{matchRallies.length} points played</span>
                        <span>{matchRallies.filter(r => r.pointWinner === 'A').length} - {matchRallies.filter(r => r.pointWinner === 'B').length} points</span>
                      </div>
                    </div>
                  </details>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {isScorer && showCompleteEventButton && (
            <button
              onClick={handleCompleteEvent}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Complete Event & Continue
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className={`${theme.background} p-4`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="w-5 h-5">
          {isScorer && currentPhase !== 'completed' && (
            <button
              onClick={handleResetMatch}
              disabled={saving || paused}
              title="Reset match"
              className="text-red-400 disabled:opacity-40"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
        <h1 className={`text-xl font-bold ${theme.text}`}>Ping Pong</h1>
        <button
          onClick={handleRefresh}
          disabled={saving}
          title="Refresh score"
          className={`${theme.textMuted} disabled:opacity-40`}
        >
          <RefreshCw className={`w-5 h-5 ${saving ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {currentPhase === 'scoring' && renderScoringPhase()}
      {currentPhase === 'completed' && renderCompletedPhase()}

      {confirmModal.isOpen && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          onConfirm={confirmModal.onConfirm}
          onClose={confirmModal.onCancel}
        />
      )}
    </div>
  );
};

export default PingPongScoring;
